import { s } from '@/theme/tokens';
import { Box, Checkbox, Text } from '@/ui/primitives';
import { TagChip } from '../tags/TagChip';
import { filterTokens } from './filterTokens';
import { TagsFilterFieldEmptyOption } from './TagsFilterFieldEmptyOption';
import { tagsFilterOptionCheckboxSx, tagsFilterOptionChipSx } from './TagsFilterFieldOption.styles';
import type { TagOption } from './TagsFilterFieldTypes';
import type { HTMLAttributes, Key, ReactElement, ReactNode } from 'react';

type TagsFilterFieldOptionProps = {
  emptyOptionsAction?: ReactNode;
  emptyOptionValue: string;
  hasCountColumn: boolean;
  multiple: boolean;
  option: TagOption;
  optionCheckboxSlotWidth: string;
  optionGridTemplateColumns?: string;
  optionFontSizePx: number;
  optionKey: Key;
  optionProps: HTMLAttributes<HTMLLIElement>;
  optionRowHeight: string;
  renderOptionLabel?: (option: TagOption) => ReactNode;
  selected: boolean;
  showOptionChips: boolean;
  actionOptionValue: string;
  onActionClick: () => void;
};

export const TagsFilterFieldOption = ({
  emptyOptionsAction,
  emptyOptionValue,
  hasCountColumn,
  multiple,
  option,
  optionCheckboxSlotWidth,
  optionGridTemplateColumns,
  optionFontSizePx,
  optionKey,
  optionProps,
  optionRowHeight,
  renderOptionLabel,
  selected,
  showOptionChips,
  actionOptionValue,
  onActionClick,
}: TagsFilterFieldOptionProps): ReactElement => {
  const isEmptyOption = option.value === emptyOptionValue;
  const isActionOption = option.value === actionOptionValue;
  const CountIcon = option.countIcon;
  const countIconSizePx = option.countIconSizePx ?? optionFontSizePx;
  const countFontSizePx = Math.max(
    11,
    Math.min(optionFontSizePx, filterTokens.optionCountFontSize),
  );
  const shouldRenderLeadingIcon =
    CountIcon !== undefined && !showOptionChips && !isEmptyOption && !isActionOption;

  return (
    <Box
      component="li"
      key={optionKey}
      {...optionProps}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      sx={{
        display: hasCountColumn ? 'grid' : 'flex',
        gridTemplateColumns: hasCountColumn
          ? (optionGridTemplateColumns ?? `${optionCheckboxSlotWidth} minmax(0, 1fr) max-content`)
          : undefined,
        alignItems: 'center',
        gap: s('sm'),
        px: s('none'),
        py: s('none'),
        width: hasCountColumn ? '100%' : undefined,
        height: optionRowHeight,
        minHeight: optionRowHeight,
        maxHeight: optionRowHeight,
        minWidth: 0,
      }}
    >
      {isEmptyOption || isActionOption || !multiple ? null : (
        <Checkbox size="small" checked={selected} sx={tagsFilterOptionCheckboxSx} />
      )}
      {isActionOption ? (
        <TagsFilterFieldEmptyOption
          action={emptyOptionsAction}
          actionPlacement="left"
          optionFontSizePx={optionFontSizePx}
          onActionClick={onActionClick}
        />
      ) : isEmptyOption ? (
        <TagsFilterFieldEmptyOption
          label={option.label}
          optionFontSizePx={optionFontSizePx}
          onActionClick={onActionClick}
        />
      ) : showOptionChips ? (
        <TagChip
          tone={option.color ?? 'neutral'}
          label={option.label}
          sx={tagsFilterOptionChipSx}
        />
      ) : (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            flex: '1 1 auto',
            gap: CountIcon ? `calc((${s('sm')} + ${s('md')}) / 2)` : s('none'),
            minWidth: 0,
            pl: CountIcon ? s('xs') : s('none'),
            width: '100%',
          }}
        >
          {shouldRenderLeadingIcon ? (
            <CountIcon
              aria-hidden="true"
              fontSize="small"
              sx={{
                width: `${countIconSizePx}px`,
                height: `${countIconSizePx}px`,
                fontSize: `${countIconSizePx}px`,
                flex: '0 0 auto',
                color: option.countIconColor ?? 'text.secondary',
              }}
            />
          ) : null}
          {renderOptionLabel ? (
            renderOptionLabel(option)
          ) : (
            <Text
              role="bodyMuted"
              color="textPrimary"
              component="span"
              sx={{
                fontSize: `${optionFontSizePx}px`,
                lineHeight: 1.4,
                fontWeight: (theme) => theme.typography.body.fontWeight,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {option.label}
            </Text>
          )}
        </Box>
      )}
      {option.count !== undefined && !isEmptyOption ? (
        <Box
          component="span"
          sx={{
            ml: hasCountColumn ? s('none') : 'auto',
            pl: hasCountColumn ? s('none') : s('lg'),
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: s('xxs'),
            minWidth: '3ch',
          }}
        >
          <Text
            role="bodyMuted"
            color="textSecondary"
            component="span"
            sx={{
              fontSize: `${countFontSizePx}px`,
              lineHeight: 1.3,
              fontWeight: (theme) => theme.typography.fontWeightRegular,
              fontVariantNumeric: 'tabular-nums',
              minWidth: '3ch',
              opacity: 0.82,
              textAlign: 'right',
            }}
          >
            {option.count.toLocaleString()}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};
